import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { AppDispatch } from 'containers/Femtasy';

import api from 'api';

import { afterSuccessSignIn } from './signIn';

interface ApiError {
  message: string;
  status?: number;
  code?: string;
  response?: {
    data?: {
      errors?: string[];
    };
  };
}

export interface SignUpState {
  email: string;
  error: ApiError | null;
  isLoading: boolean;
  isLoadingFb: boolean;
  success: boolean;
  termsAccepted: boolean;
  token: string | null;
}

export const initialSignUpState: SignUpState = {
  email: '',
  error: null,
  isLoading: false,
  isLoadingFb: false,
  success: false,
  termsAccepted: false,
  token: null,
};

const signUpSlice = createSlice({
  name: 'signUp',
  initialState: initialSignUpState,
  reducers: {
    signUpLoading: (state) => {
      state.error = null;
      state.isLoading = true;
      state.success = false;
    },
    updateEmail: {
      reducer: (state, action: PayloadAction<{ email: string }>) => {
        state.isLoading = false;
        state.email = action.payload.email;
      },
      prepare: (email: string) => ({ payload: { email } }),
    },
    acceptTerms: {
      reducer: (state, action: PayloadAction<{ termsAccepted: boolean }>) => {
        state.termsAccepted = action.payload.termsAccepted;
      },
      prepare: (termsAccepted: boolean) => ({ payload: { termsAccepted } }),
    },
    signUpLoadingFb: (state) => {
      state.error = null;
      state.isLoading = false;
      state.isLoadingFb = true;
      state.success = false;
    },
    signUpSuccess: (state) => {
      state.isLoading = false;
      state.isLoadingFb = false;
      state.success = true;
    },
    signUpError: {
      reducer: (state, action: PayloadAction<ApiError>) => {
        state.isLoading = false;
        state.isLoadingFb = false;
        state.error = action.payload;
      },
      prepare: (error?: unknown) => {
        const axiosError = error as AxiosError<{ errors?: string[] }>;

        return {
          payload: {
            message: axiosError?.message,
            status: axiosError?.response?.status,
            code: axiosError?.code,
            response: {
              data: {
                errors: axiosError?.response?.data?.errors,
              },
            },
          },
        };
      },
    },
    resetSignUp: () => initialSignUpState,
  },
});

export const {
  signUpLoading,
  updateEmail,
  acceptTerms,
  signUpLoadingFb,
  signUpSuccess,
  signUpError,
  resetSignUp,
} = signUpSlice.actions;

export default signUpSlice.reducer;

export const signUpAndLogin =
  (values: Record<string, unknown>) => async (dispatch: AppDispatch) => {
  dispatch(signUpLoading());

  try {
    const res = await api.auth.signUp(values);

    dispatch(signUpSuccess());
    afterSuccessSignIn(dispatch, res);
  } catch (err) {
    dispatch(signUpError(err as AxiosError));
  }
};
